import React, { useEffect, useLayoutEffect } from "react";
import { Box, Grid, ToggleButton, ToggleButtonGroup, Typography } from "@mui/material";
import { Feature, FilterCondition, SidebarFilters } from "types/gql-types";
import { useSidebarFilters } from "../sidebar-filters";
import { useInterestsTable } from "../interests-table/table-context";

type Props = { lists: SidebarFilters["interestListsById"]; available: boolean };

const FilterLogicControl: React.FC<Props> = ({ lists, available }) => {
  const { filterCondition, setFilterCondition } = useSidebarFilters();
  const { deselectAllInterests } = useInterestsTable();
  const enabled = available && lists.length > 0 && lists.every((list) =>
    list.property.features.some((feature) => feature.name === Feature.FILTER_LOGIC));

  useLayoutEffect(() => {
    if (enabled && !filterCondition) setFilterCondition(FilterCondition.AND, false);
  }, [enabled, filterCondition, setFilterCondition]);

  useEffect(() => {
    if (!enabled && filterCondition) setFilterCondition();
  }, [enabled, filterCondition, setFilterCondition]);

  if (!enabled) return null;

  const condition = filterCondition || FilterCondition.AND;
  return (
    <Grid size={{ xs: 12 }} sx={{ mt: 1.5 }}>
      <ToggleButtonGroup
        id="filter-logic"
        exclusive
        fullWidth
        size="small"
        value={condition}
        aria-label="Income range rule"
        aria-describedby="income-rule-explanation income-rule-scope"
        onChange={(_event, next: FilterCondition | null) => {
          if (next && next !== condition) {
            deselectAllInterests();
            setFilterCondition(next);
          }
        }}
      >
        <ToggleButton value={FilterCondition.AND} aria-label="Require income range (AND)">
          Require range · AND
        </ToggleButton>
        <ToggleButton value={FilterCondition.OR} aria-label="Income range or subsidy (OR)">
          Or subsidy · OR
        </ToggleButton>
      </ToggleButtonGroup>
      <Box sx={{ mt: 1 }}>
        <Typography id="income-rule-explanation" variant="body2" aria-live="polite">
          {condition === FilterCondition.AND ? "Income must be in range, even with a subsidy." : "Income is in range OR the household has a subsidy."}
        </Typography>
        <Typography id="income-rule-scope" variant="caption" sx={{ display: "block", mt: 0.5 }}>
          Applies to income only. Other filters keep their existing rules.
        </Typography>
      </Box>
    </Grid>
  );
};

export default FilterLogicControl;
